import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  LinearProgress,
  Divider,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Psychology,
  Timeline,
  Recommend,
  Warning,
  BugReport,
  TrendingUp,
  ExpandMore,
  CheckCircle,
  HighlightOff,
  Schedule,
  Assessment,
} from '@mui/icons-material';
import { AIAnalysisResult } from '../services/aiAnalysisService';

interface AIInsightsDisplayProps {
  analysisResult: AIAnalysisResult;
  loading?: boolean;
}

const AIInsightsDisplay: React.FC<AIInsightsDisplayProps> = ({ 
  analysisResult, 
  loading = false 
}) => {
  // Helper function to get priority color
  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'high':
        return '#f44336';
      case 'medium':
        return '#ff9800';
      case 'low':
        return '#4caf50';
      default:
        return '#2196f3';
    }
  };

  // Helper function to get confidence color
  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 0.8) return '#4caf50';
    if (confidence >= 0.6) return '#ff9800';
    return '#f44336';
  };

  // Helper function to get risk score color
  const getRiskColor = (score: number) => {
    if (score >= 0.7) return '#f44336';
    if (score >= 0.4) return '#ff9800';
    return '#4caf50';
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Psychology sx={{ color: '#0067b3' }} />
          Analyzing alert data with AI...
        </Typography>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          fontWeight: 600,
          color: '#0067b3'
        }}>
          <Psychology />
          AI Analysis Results
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
          Comprehensive AI-driven insights for {analysisResult.scope_name} • 
          Generated in {analysisResult.analysis_duration_seconds}s • 
          {new Date(analysisResult.analysis_timestamp).toLocaleString()}
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Prediction Analysis */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '1px solid #e1e5e9', borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  🔮 Prediction
                </Typography>
              </Box>
              
              <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.6 }}>
                {analysisResult.prediction.predicted_outcome}
              </Typography>
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>
                  TIME TO IMPACT
                </Typography>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Schedule fontSize="small" />
                  {analysisResult.prediction.time_to_impact}
                </Typography>
              </Box>
              
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>
                  IMPACT SCOPE
                </Typography>
                <Typography variant="body2">
                  {analysisResult.prediction.impact_scope}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Recommendation */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '1px solid #e1e5e9', borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  🛠️ Recommendation
                </Typography>
                <Chip 
                  label={analysisResult.recommendation.action_priority}
                  size="small"
                  sx={{ 
                    ml: 'auto',
                    backgroundColor: getPriorityColor(analysisResult.recommendation.action_priority),
                    color: 'white'
                  }}
                />
              </Box>
              
              <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.6 }}>
                {analysisResult.recommendation.recommended_action}
              </Typography>
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>
                  EXPECTED RESOLUTION TIME
                </Typography>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Schedule fontSize="small" />
                  {analysisResult.recommendation.expected_resolution_time}
                </Typography>
              </Box>
              
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>
                  SUCCESS RATE
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={analysisResult.recommendation.success_rate * 100}
                    sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {Math.round(analysisResult.recommendation.success_rate * 100)}%
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Escalation Risk */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '1px solid #e1e5e9', borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  ⚠️ Escalation Risk
                </Typography>
                <Chip 
                  label={`${Math.round(analysisResult.escalation_risk.escalation_risk_score * 100)}% risk`}
                  size="small"
                  sx={{ 
                    ml: 'auto',
                    backgroundColor: getRiskColor(analysisResult.escalation_risk.escalation_risk_score),
                    color: 'white'
                  }}
                />
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>
                  RISK SCORE
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={analysisResult.escalation_risk.escalation_risk_score * 100}
                    sx={{ 
                      flexGrow: 1, 
                      height: 12, 
                      borderRadius: 6,
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: getRiskColor(analysisResult.escalation_risk.escalation_risk_score)
                      }
                    }}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {(analysisResult.escalation_risk.escalation_risk_score * 100).toFixed(1)}%
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>
                  HISTORICAL ESCALATION RATE
                </Typography>
                <Typography variant="body2">
                  {analysisResult.escalation_risk.historical_escalation_rate}
                </Typography>
              </Box>
              
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>
                  PREVENTIVE ACTION
                </Typography>
                <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                  {analysisResult.escalation_risk.recommended_preventive_action}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Root Cause Analysis */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '1px solid #e1e5e9', borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  🧠 Root Cause Analysis
                </Typography>
              </Box>
              
              <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.6 }}>
                {analysisResult.root_cause.predicted_root_cause}
              </Typography>
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>
                  RELATED COMPONENTS
                </Typography>
                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {analysisResult.root_cause.related_components.map((component, index) => (
                    <Chip 
                      key={index}
                      label={component}
                      size="small"
                      variant="outlined"
                      sx={{ 
                        borderColor: '#795548',
                        color: '#795548'
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Reliability Improvement */}
        <Grid item xs={12}>
          <Card sx={{ border: '1px solid #e1e5e9', borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  📈 Reliability Improvement Suggestions
                </Typography>
                <Chip 
                  label={analysisResult.reliability_improvement.priority_level}
                  size="small"
                  sx={{ 
                    ml: 'auto',
                    backgroundColor: getPriorityColor(analysisResult.reliability_improvement.priority_level),
                    color: 'white'
                  }}
                />
              </Box>
              
              <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.6 }}>
                {analysisResult.reliability_improvement.recommendation}
              </Typography>
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600 }}>
                  IMPACT ESTIMATE
                </Typography>
                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Assessment fontSize="small" />
                  {analysisResult.reliability_improvement.impact_estimate}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Summary Alert */}
      <Alert 
        severity="info" 
        sx={{ mt: 3, borderRadius: 2 }}
        icon={<Psychology />}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          AI Analysis Summary
        </Typography>
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          {analysisResult.performance_summary}
        </Typography>
      </Alert>
    </Box>
  );
};

export default AIInsightsDisplay;